import { useState, useEffect } from "react";
import { apiFetch } from "../../../api";
import s from "./ProfilePanel.module.css";

const ICONS = {
  user: "M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z",
  mail: "M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75",
  calendar: "M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5",
  crown: "M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z",
  shield: "M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z",
};

function Icon({ name, size = 20 }) {
  const d = ICONS[name];
  if (!d) return null;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  );
}

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" });
}

function daysUntil(dateStr) {
  const diff = new Date(dateStr) - new Date();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export default function ProfilePanel() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function fetchProfile() {
      try {
        const res = await apiFetch("/api/v1/profile");
        if (!res.ok) throw new Error("Failed to load profile");
        const data = await res.json();
        if (!cancelled) setProfile(data);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchProfile();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className={s.panel}>
        <div className={s.header}>
          <div className={s.headerContent}>
            <div className={s.headerIconWrap}><Icon name="user" /></div>
            <div>
              <h2 className={s.headerTitle}>Profile</h2>
              <p className={s.headerSub}>Your account & subscription</p>
            </div>
          </div>
        </div>
        <div className={s.scrollArea}>
          <div className={s.loadingMsg}>Loading profile...</div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className={s.panel}>
        <div className={s.header}>
          <div className={s.headerContent}>
            <div className={s.headerIconWrap}><Icon name="user" /></div>
            <div>
              <h2 className={s.headerTitle}>Profile</h2>
              <p className={s.headerSub}>Your account & subscription</p>
            </div>
          </div>
        </div>
        <div className={s.scrollArea}>
          <div className={s.errorMsg}>{error || "Could not load profile"}</div>
        </div>
      </div>
    );
  }

  const initial = profile.email ? profile.email.charAt(0) : "?";
  const sub = profile.subscription;
  const isPremium = sub?.is_premium ?? false;
  const daysLeft = sub?.premium_until ? daysUntil(sub.premium_until) : 0;

  const LEVEL_LABELS = { beginner: "Beginner", some_experience: "Some experience", intermediate: "Intermediate" };
  const REFERRAL_LABELS = { google: "Google", salman: "Heard from Salman", friend: "A friend", social_media: "Social media", other: "Other" };
  const COUNTRY_NAMES = { IR: "Iran", SE: "Sweden", US: "USA", GB: "United Kingdom", DE: "Germany", FR: "France", NO: "Norway", DK: "Denmark", FI: "Finland", NL: "Netherlands", ES: "Spain", IT: "Italy", TR: "Turkey", PL: "Poland", IQ: "Iraq", SY: "Syria", AF: "Afghanistan", IN: "India", CN: "China", JP: "Japan", KR: "South Korea", BR: "Brazil", CA: "Canada", AU: "Australia", EG: "Egypt", RU: "Russia", UA: "Ukraine", TH: "Thailand", PH: "Philippines", SO: "Somalia", ER: "Eritrea", ET: "Ethiopia", CL: "Chile", CO: "Colombia", MX: "Mexico", AR: "Argentina", PT: "Portugal", GR: "Greece", RO: "Romania", HU: "Hungary", CZ: "Czech Republic", AT: "Austria", CH: "Switzerland", BE: "Belgium", IE: "Ireland", NZ: "New Zealand", ZA: "South Africa", NG: "Nigeria", KE: "Kenya", PK: "Pakistan", BD: "Bangladesh", VN: "Vietnam", ID: "Indonesia", MY: "Malaysia", SG: "Singapore", IL: "Israel", LB: "Lebanon", SA: "Saudi Arabia", AE: "UAE" };

  return (
    <div className={s.panel}>
      {/* Header */}
      <div className={s.header}>
        <div className={s.headerContent}>
          <div className={s.headerIconWrap}>
            <Icon name="user" />
          </div>
          <div>
            <h2 className={s.headerTitle}>Profile</h2>
            <p className={s.headerSub}>Your account & subscription</p>
          </div>
          {isPremium && (
            <div className={s.headerPlanPill}>
              <Icon name="crown" size={12} />
              <span>Premium</span>
            </div>
          )}
        </div>
      </div>

      <div className={s.scrollArea}>
        {/* Hero Card */}
        <div className={s.heroCard}>
          <div className={s.heroGlow} />
          <div className={s.avatar}>
            <span className={s.avatarInitial}>{initial}</span>
            <div className={s.avatarRing} />
          </div>
          <div className={s.heroInfo}>
            <div className={s.heroName}>{profile.email}</div>
            <div className={s.heroDetails}>
              <span className={s.heroDetail}><Icon name="calendar" size={12} /> Joined {formatDate(profile.joined)}</span>
              <span className={s.heroDetail}>
                <Icon name="shield" size={12} />
                {isPremium ? "Premium member" : "Free plan"}
              </span>
            </div>
          </div>
        </div>

        {/* Subscription */}
        <div className={s.section}>
          <h3 className={s.sectionTitle}>Subscription</h3>
          <div className={s.subCard}>
            <div className={s.subTop}>
              <div className={s.subPlanWrap}>
                <div className={s.subPlanIcon}>
                  <Icon name={isPremium ? "crown" : "shield"} size={18} />
                </div>
                <div>
                  <div className={s.subPlanName}>{isPremium ? "Premium" : "Free"}</div>
                </div>
              </div>
              <div className={`${s.subStatus} ${isPremium ? s.subActive : s.subExpired}`}>
                <div className={s.subStatusDot} />
                {isPremium ? "Active" : "Inactive"}
              </div>
            </div>

            {sub && (
              <div className={s.subTimeline}>
                <div className={s.subTimelineRow}>
                  <span className={s.subTimelineLabel}>{isPremium ? "Expires" : "Expired"}</span>
                  <span className={s.subTimelineValue}>{sub.premium_until ? formatDate(sub.premium_until) : "\u2014"}</span>
                </div>
              </div>
            )}

            {isPremium && daysLeft > 0 && (
              <div className={s.subProgress}>
                <div className={s.subProgressTop}>
                  <span className={s.subDaysLeft}>
                    <strong>{daysLeft}</strong> days remaining
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Onboarding Info */}
        {profile.onboarding_completed && (
          <div className={s.section} style={{ animationDelay: "0.3s" }}>
            <h3 className={s.sectionTitle}>About you</h3>
            <div className={s.aboutCard}>
              {profile.country_of_origin && (
                <div className={s.aboutRow}>
                  <span className={s.aboutLabel}>Country</span>
                  <span className={s.aboutValue}>{COUNTRY_NAMES[profile.country_of_origin] || profile.country_of_origin}</span>
                </div>
              )}
              {profile.swedish_level && (
                <div className={s.aboutRow}>
                  <span className={s.aboutLabel}>Level</span>
                  <span className={s.aboutValue}>{LEVEL_LABELS[profile.swedish_level] || profile.swedish_level}</span>
                </div>
              )}
              {profile.referral_source && (
                <div className={s.aboutRow}>
                  <span className={s.aboutLabel}>Found us via</span>
                  <span className={s.aboutValue}>{REFERRAL_LABELS[profile.referral_source] || profile.referral_source}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
